import { CircularProgress } from "@mui/material";
import classes from "../homePageNew.module.css";
import { SearchIcon, HistoryIcon } from "./icons";
import RecordCard from "./RecordCard";
import PaginationBar from "./PaginationBar";
import ThreeDotLoader from "./ThreeDotLoader";

// "Search full History" link is disabled for now — flip to re-enable.
const SHOW_HISTORY_LINK = false;

/**
 * Generic list pane, reusable by every future module: search box + card list
 * + PaginationBar (+ a "Search full History" link, currently disabled).
 * Which data source (main list vs. active search) it's showing is entirely
 * decided by the caller — this component only renders whatever
 * records/pagination props it's given.
 */
export default function ModuleListPane({
  moduleLabel,
  records,
  selectedId,
  onSelect,
  onRetried,
  query,
  onQueryChange,
  loading,
  page,
  totalPages,
  total,
  limit,
  onPageChange,
  totalHistoryCount,
  onOpenHistory,
  variant = "brand",
  noDataAtAll = false,
}) {
  const rangeStart = total > 0 ? (page - 1) * limit : 0;

  return (
    <div className={classes.mdListPane}>
      {loading && records.length > 0 && (
        <div className={classes.mdListLoadingOverlay}>
          <ThreeDotLoader />
        </div>
      )}
      <div className={classes.mdSearch}>
        <div className={classes.mdSearchBox}>
          <SearchIcon size={14} />
          <input
            placeholder={`Search ${moduleLabel}…`}
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
          />
        </div>
      </div>
      {/* Cards + pager (+ history link) share ONE scroll region, sized to
          fill whatever's left of the pane, rather than each having its own
          box — that's what used to leave a dead gap between a short card
          list (Category Analysis's shorter, avatar-less cards especially)
          and the pager: .mdListScroll alone used to flex-grow to fill the
          pane, so the pager (a sibling after it) got pushed all the way
          down to the bottom of that grown box instead of sitting right
          after the last card. Now the pager sits immediately after the
          cards in normal flow, and any leftover height (e.g. a shorter
          Category Analysis pane matched to a taller detail pane on the
          right) shows below the pager instead — background, not a gap
          mid-list. Still scrolls as a unit on the rare page tall enough to
          need it (nothing here assumes a fixed record count). */}
      <div className={classes.mdListBody}>
        <div className={classes.mdListScroll}>
          {loading && records.length === 0 ? (
            <div className={classes.loadingWrap} style={{ padding: "40px 0" }}>
              <CircularProgress size={22} />
            </div>
          ) : records.length === 0 ? (
            <div className={classes.mdEmptyList}>
              {noDataAtAll ? `No ${moduleLabel} runs yet` : `No matches${query ? ` for "${query}"` : ""}`}
            </div>
          ) : (
            records.map((record) => (
              <RecordCard
                key={record.transactionId}
                record={record}
                selected={String(record.transactionId) === String(selectedId)}
                onSelect={onSelect}
                onRetried={onRetried}
                variant={variant}
              />
            ))
          )}
        </div>
        <div className={classes.mdPagerWrap}>
          {total > 0 && (
            <PaginationBar
              page={page}
              totalPages={totalPages}
              total={total}
              start={rangeStart}
              count={records.length}
              onPageChange={onPageChange}
            />
          )}
        </div>
        {SHOW_HISTORY_LINK && (
          <button className={classes.mdHistoryLink} onClick={onOpenHistory}>
            <HistoryIcon size={13} /> Search full History ({totalHistoryCount})
          </button>
        )}
      </div>
    </div>
  );
}
